import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import type { SxProps } from '@mui/system';
import { BiAddToQueue } from 'react-icons/bi';
import { getType } from 'mime';

import DragAndDropField from 'components/DragAndDropField';
import FileItem from 'components/FileItem';
import useFiles from 'hooks/useFiles';
import { IFolder } from 'typings/document';
import { ReactComponent as SSLSecureIcon } from 'assets/icons/ssl-secure.svg';
import useUploadFiles, {
  checkFileSize,
  MAXIMUM_FILE_SIZE,
} from 'hooks/useUploadFiles';
import { boldenLastWord, humanFileSize } from 'utils/helpers';

interface IUploadFilesModalProps {
  open: boolean;
  // eslint-disable-next-line no-unused-vars
  setOpen: (arg0: boolean) => void;
  folder: IFolder;
  reference: string;
}

const buttonExtraStyles: SxProps = {
  width: 1,
  py: 1,
  mb: 0.5,
  fontWeight: 'normal',
  flexGrow: 1,
  justifyContent: 'space-between',
  '& .MuiButton-endIcon > svg': {
    fontSize: 'h5.fontSize',
    opacity: 0.7,
  },
};

const UploadFilesModal: FC<IUploadFilesModalProps> = ({
  open,
  setOpen,
  folder,
  reference,
}) => {
  const { t: translate } = useTranslation();
  const [files, filesActionsBag] = useFiles();
  const { addRawFiles, removeFiles } = filesActionsBag;
  const { onSubmit, onCancel, handleAddFiles, isSubmitting } = useUploadFiles({
    files,
    filesActionsBag,
    folder,
    setOpen,
    reference,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const folderMimeTypes = folder.fileExtensions?.map(getType) ?? [];
  const fileInputAccept = folderMimeTypes.join(', ');

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onCancel}
      aria-labelledby="documents-upload-dialog-title"
      aria-describedby="documents-upload-dialog-description"
      PaperProps={{
        sx: {
          p: 0,
          minWidth: [undefined, undefined, undefined, 1 / 2.5],
          minHeight: [undefined, undefined, undefined, 500],
        },
      }}
      scroll="body"
      disableEscapeKeyDown
    >
      <DialogTitle
        id="documents-upload-dialog-title"
        sx={{
          width: 1,
          display: 'inline-flex',
          p: 2,
        }}
      >
        <Stack position="absolute">
          <SSLSecureIcon width="3rem" height="1.5rem" />
        </Stack>

        <Stack component="span" mx="auto">
          {translate('UploadModal.UploadYourFiles')}
        </Stack>

        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'common.grey',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <DialogContentText id="documents-upload-dialog-description" pb={3}>
          {translate('UploadModal.AcceptedDocumentTypes')}:{' '}
          {(folder?.fileExtensions?.length ?? 0) > 0
            ? `.${folder?.fileExtensions?.join(', .').toUpperCase()}`
            : translate('Common.All')}
          <br />
          {translate('UploadModal.MaximalFileSizeAccepted')}:&nbsp;
          {humanFileSize(MAXIMUM_FILE_SIZE * 10 ** 6, true)}.
        </DialogContentText>

        <Stack direction="row" justifyContent="space-between" pb={1}>
          <Typography variant="body2" fontWeight={700}>
            {translate('Common.Type')}:&nbsp;
            <Typography component="span" color="primary.main" fontWeight={700}>
              {folder.name}
            </Typography>
          </Typography>

          <Typography variant="body2" fontWeight={700}>
            {translate('Common.Reference')}:&nbsp;
            <Typography component="span" color="primary.main" fontWeight={700}>
              {reference}
            </Typography>
          </Typography>
        </Stack>

        <DragAndDropField
          onDrop={addRawFiles}
          disabled={isSubmitting}
          accept={fileInputAccept}
        />

        <Stack pt={1.5}>
          <Button
            endIcon={<BiAddToQueue />}
            sx={buttonExtraStyles}
            onClick={() => fileInputRef.current?.click()}
          >
            <span>{boldenLastWord(translate('Common.AddFilesManually'))}</span>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAddFiles}
              accept={fileInputAccept}
              disabled={isSubmitting}
              multiple
              hidden
            />
          </Button>
        </Stack>

        {files.length > 0 && (
          <Stack pt={2}>
            <Typography
              color="common.grey"
              fontSize="0.9375rem"
              fontWeight="bold"
              pb={1}
            >
              {translate('Common.AddedFiles')}:
            </Typography>

            {files.map((file) => {
              let error;
              const {
                id,
                content: { name, type },
                progress,
              } = file;

              const hasNetworkError = file.progress === -1;
              const hasCancelError = file.progress === -2;
              const hasFileSizeError = !checkFileSize(file.content);

              if (hasNetworkError) {
                error = translate('Common.NetworkError');
              }

              if (hasFileSizeError) {
                error = translate('Common.FileSizeError');
              }

              if (hasCancelError) {
                error = translate('Common.OperationCancelledError');
              }

              return (
                <Stack key={id} pt={0.5}>
                  <FileItem
                    name={name}
                    extension={type}
                    progress={progress}
                    onDelete={() => {
                      if (file.cancel) {
                        file.cancel();
                      } else {
                        removeFiles([file]);
                      }
                    }}
                    retry={
                      hasNetworkError || hasCancelError
                        ? () => onSubmit([file])
                        : undefined
                    }
                    error={error}
                  />
                </Stack>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      {files.length > 0 && (
        <Stack bgcolor="primary.main" position="sticky" bottom={0}>
          <Typography
            variant="h6"
            color="common.white"
            pt={1.5}
            pb={1}
            align="center"
          >
            {translate('UploadModal.AddToFor', {
              documentType: folder.name,
              reference,
            })}
          </Typography>

          <Typography
            variant="body1"
            color="common.white"
            px={[1, undefined, undefined, 2]}
            pb={1}
            align="center"
            sx={{ fontStyle: 'italic' }}
          >
            {translate('UploadModal.SubmitNote')}
          </Typography>

          <DialogActions
            sx={{
              pt: 1.5,
              justifyContent: 'space-between',
              p: [1, undefined, undefined, 2],
            }}
          >
            <Button
              variant="text"
              color="secondary"
              sx={{
                fontWeight: 600,
                textDecoration: 'underline',
                textUnderlineOffset: '0.2rem',
                textTransform: 'capitalize',
              }}
              onClick={onCancel}
            >
              {translate('Common.Cancel')}
            </Button>
            <LoadingButton
              sx={{
                justifyContent: 'space-between',
                width: 0.5,
                minWidth: 150,
                maxWidth: '16rem',
                px: 3,
                py: [0.75, undefined, undefined, 1.5],
                color: 'primary.main',
                '&:hover': {
                  color: 'white',
                },
                '& .MuiLoadingButton-loadingIndicator': {
                  position: 'relative',
                  inset: 0,
                },
              }}
              endIcon={isSubmitting ? undefined : <CheckOutlinedIcon />}
              onClick={() => onSubmit()}
              loading={isSubmitting}
              loadingPosition="end"
              variant="contained"
              color="secondary"
            >
              {translate('Common.AddToMyVault')}
            </LoadingButton>
          </DialogActions>
        </Stack>
      )}
    </Dialog>
  );
};

export default UploadFilesModal;
